import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  Grid,
} from '@mui/material';
import { FilterList, Error, CheckCircle, Pending, Warning, Schedule, Person } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GetPickingsVm } from 'api/picking/get-pickings.vm';
import { getPickings } from 'api/picking/get-pickings';
import { PickingVm, OrderPickingStatus } from 'api/picking/picking.vm';
import { GetWorkerVm } from 'api/management/get-worker.vm';
import { getWorker } from 'api/management/get-worker';
import { GetWarehousesVm } from 'api/warehouse/get-warehouses.vm';
import { getWarehouses } from 'api/warehouse/get-warehouses';
import WorkerSelectionDialog from 'components/WorkerSelectionDialog';

const PickingProcessesPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pickings, setPickings] = useState<PickingVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [pickerDialogOpen, setPickerDialogOpen] = useState(false);
  const [selectedPicker, setSelectedPicker] = useState<GetWorkerVm | null>(null);
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);

  // Estado inicial dos filtros da URL
  const statusFilter = (searchParams.get('status') as OrderPickingStatus | null) || null;
  const warehouseFilter = searchParams.get('warehouse') || '';
  const pickerFilter = searchParams.get('picker') || '';

  const updateSearchParams = (newParams: { status?: OrderPickingStatus | null; warehouse?: string; picker?: string }) => {
    const params = new URLSearchParams(searchParams);

    if (newParams.status !== undefined) {
      newParams.status ? params.set('status', newParams.status) : params.delete('status');
    }
    if (newParams.warehouse !== undefined) {
      newParams.warehouse ? params.set('warehouse', newParams.warehouse) : params.delete('warehouse');
    }
    if (newParams.picker !== undefined) {
      newParams.picker ? params.set('picker', newParams.picker) : params.delete('picker');
    }

    setSearchParams(params);
  };

  const fetchPickings = (offset: string | null, reset: boolean = false) => {
    setLoading(true);

    getPickings(offset || '', statusFilter || null, null, null, pickerFilter || null, warehouseFilter || null, {
      success: (response: GetPickingsVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setPickings(reset ? response.objects : [...pickings, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter processos de picking:', err);
        setLoading(false);
      },
    });
  };

  const fetchWarehouses = () => {
    getWarehouses('', {
      success: (response: GetWarehousesVm) => {
        setWarehouses(response.objects);
      },
      error: (err) => {
        console.error('Falha ao obter armazéns:', err);
      },
    });
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    fetchPickings(null, true);
  }, [statusFilter, warehouseFilter, pickerFilter]);

  // Load picker from URL parameter
  useEffect(() => {
    if (pickerFilter && !selectedPicker) {
      getWorker(pickerFilter, {
        success: (worker) => {
          setSelectedPicker(worker);
        },
        error: (err) => {
          console.error('Falha ao carregar separador:', err);
        },
      });
    } else if (!pickerFilter && selectedPicker) {
      setSelectedPicker(null);
    }
  }, [pickerFilter]);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchPickings(nextOffset);
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleStatusFilter = (status: OrderPickingStatus | null) => {
    updateSearchParams({ status });
    handleFilterClose();
  };

  const handlePickerFilter = (picker: GetWorkerVm | null) => {
    setSelectedPicker(picker);
    updateSearchParams({ picker: picker?.id || '' });
    setPickerDialogOpen(false);
  };

  const getStatusLabel = (status: OrderPickingStatus) => {
    switch (status) {
      case OrderPickingStatus.IN_PROGRESS:
        return 'Em Progresso';
      case OrderPickingStatus.COMMITTED:
        return 'Confirmado';
      case OrderPickingStatus.CANCELED:
        return 'Cancelado';
      case OrderPickingStatus.DOCUMENT_CREATION_FAIL:
        return 'Erro na Criação';
      case OrderPickingStatus.DOCUMENT_CREATION_SUCCESS:
        return 'Documento Criado';
      default:
        return status;
    }
  };

  const getStatusChip = (status: OrderPickingStatus) => {
    switch (status) {
      case OrderPickingStatus.IN_PROGRESS:
        return <Chip icon={<Schedule fontSize="small" />} label="Em Progresso" color="warning" size="small" />;
      case OrderPickingStatus.COMMITTED:
        return <Chip icon={<CheckCircle fontSize="small" />} label="Confirmado" color="success" size="small" />;
      case OrderPickingStatus.CANCELED:
        return <Chip icon={<Error fontSize="small" />} label="Cancelado" color="error" size="small" />;
      case OrderPickingStatus.DOCUMENT_CREATION_FAIL:
        return <Chip icon={<Error fontSize="small" />} label="Erro na Criação" color="error" size="small" />;
      case OrderPickingStatus.DOCUMENT_CREATION_SUCCESS:
        return <Chip icon={<CheckCircle fontSize="small" />} label="Documento Criado" color="success" size="small" />;
      default:
        return <Chip label="Desconhecido" size="small" />;
    }
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Processos de Picking
        </Typography>
      </Box>

      <Paper sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: theme.shadows[1] }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel id="warehouse-select-label">Armazém</InputLabel>
              <Select
                labelId="warehouse-select-label"
                value={warehouseFilter || ''}
                label="Armazém"
                onChange={(e) => {
                  updateSearchParams({ warehouse: e.target.value });
                }}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>Todos os Armazéns</Box>
                </MenuItem>
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{warehouse.name}</Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {statusFilter && <Chip label={getStatusLabel(statusFilter)} onDelete={() => handleStatusFilter(null)} sx={{ mr: 1 }} />}
                <IconButton onClick={handleFilterClick} color="primary">
                  <FilterList />
                </IconButton>
                <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={handleFilterClose}>
                  <MenuItem onClick={() => handleStatusFilter(null)}>Todos os estados</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter(OrderPickingStatus.IN_PROGRESS)}>Em Progresso</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter(OrderPickingStatus.COMMITTED)}>Confirmado</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter(OrderPickingStatus.CANCELED)}>Cancelado</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter(OrderPickingStatus.DOCUMENT_CREATION_FAIL)}>Erro na Criação</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter(OrderPickingStatus.DOCUMENT_CREATION_SUCCESS)}>Documento Criado</MenuItem>
                </Menu>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {selectedPicker ? (
                  <Chip
                    icon={<Person fontSize="small" />}
                    label={selectedPicker.name}
                    onDelete={() => handlePickerFilter(null)}
                    sx={{ mr: 1 }}
                  />
                ) : (
                  <Button variant="outlined" onClick={() => setPickerDialogOpen(true)} sx={{ mr: 1 }}>
                    Filtrar por Separador
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Pedido</TableCell>
              <TableCell>Data de Criação</TableCell>
              <TableCell>Separador</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pickings.map((picking) => (
              <TableRow
                key={picking.id}
                hover
                onClick={() => navigate(`/shipping/picking-processes/${picking.id}`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{picking.orderId}</TableCell>
                <TableCell>{formatDate(picking.createdAt)}</TableCell>
                <TableCell>{picking.pickerName}</TableCell>
                <TableCell>{getStatusChip(picking.status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {hasMore && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Button variant="outlined" onClick={handleLoadMore}>
            Carregar Mais
          </Button>
        </Box>
      )}

      <WorkerSelectionDialog
        open={pickerDialogOpen}
        onClose={() => setPickerDialogOpen(false)}
        onSelect={handlePickerFilter}
        selectedWorkerId={selectedPicker?.id}
        title="Selecionar Separador"
      />
    </Box>
  );
};

export default PickingProcessesPage;
